import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import AppLayout from '../components/layout/AppLayout'
import Landing from '../pages/landing/Landing'
import ResetPassword from '../pages/auth/ResetPassword'
import UserAgreementPage from '../pages/legal/UserAgreementPage'
import PrivacyPolicyPage from '../pages/legal/PrivacyPolicyPage'
import DisclaimerPage from '../pages/legal/DisclaimerPage'
import AcceptableUsePolicyPage from '../pages/legal/AcceptableUsePolicyPage'
import MerchantServicesAgreementPage from '../pages/legal/MerchantServicesAgreementPage'
import Dashboard from '../pages/dashboard/Dashboard'
import ProductList from '../pages/products/ProductList'
import ProductCreate from '../pages/products/ProductCreate'
import ProductEdit from '../pages/products/ProductEdit'
import OrderList from '../pages/orders/OrderList'
import OrderDetail from '../pages/orders/OrderDetail'
import PaymentMethods from '../pages/payment-methods/PaymentMethods'
import Balance from '../pages/balance/Balance'
import Transactions from '../pages/balance/Transactions'
import Refunds from '../pages/balance/Refunds'
import PayoutList from '../pages/payouts/PayoutList'
import PayoutCreate from '../pages/payouts/PayoutCreate'
import BankAccounts from '../pages/payouts/BankAccounts'
import KybSubmit from '../pages/kyc/KybSubmit'
import KycSubmit from '../pages/kyc/KycSubmit'
import ApiKeys from '../pages/developers/ApiKeys'
import Webhooks from '../pages/developers/Webhooks'
import Settings from '../pages/settings/Settings'
import AnnouncementCenter from '../pages/announcements/AnnouncementCenter'
import SubscriptionList from '../pages/subscriptions/SubscriptionList'
import SubscriptionDetail from '../pages/subscriptions/SubscriptionDetail'
import DisputeList from '../pages/disputes/DisputeList'

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  return isAuthenticated ? <>{children}</> : <Navigate to="/" replace />
}

function LandingRoute() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Landing />
}

function RegisterRedirectRoute() {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const inviteCode =
    queryParams.get('code')?.trim() ||
    queryParams.get('inviteCode')?.trim()
  const targetParams = new URLSearchParams()
  if (inviteCode) {
    targetParams.set('code', inviteCode)
  } else {
    targetParams.set('mode', 'register')
  }
  return <Navigate to={`/?${targetParams.toString()}`} replace />
}

export default function AppRouter() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingRoute />} />
      <Route path="/login" element={<Landing />} />
      <Route path="/register" element={<RegisterRedirectRoute />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/legal/user-agreement" element={<UserAgreementPage />} />
      <Route path="/legal/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/legal/disclaimer" element={<DisclaimerPage />} />
      <Route path="/legal/acceptable-use-policy" element={<AcceptableUsePolicyPage />} />
      <Route path="/legal/merchant-services-agreement" element={<MerchantServicesAgreementPage />} />

      {/* Private routes */}
      <Route path="/dashboard" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="products" element={<ProductList />} />
        <Route path="products/new" element={<ProductCreate />} />
        <Route path="products/:id" element={<ProductEdit />} />
        <Route path="orders" element={<OrderList />} />
        <Route path="disputes" element={<DisputeList />} />
        <Route path="orders/:id" element={<OrderDetail />} />
        <Route path="payment-methods" element={<PaymentMethods />} />
        <Route path="subscriptions" element={<SubscriptionList />} />
        <Route path="subscriptions/:id" element={<SubscriptionDetail />} />
        <Route path="balance" element={<Balance />} />
        <Route path="balance/transactions" element={<Transactions />} />
        <Route path="balance/refunds" element={<Refunds />} />
        <Route path="payouts" element={<PayoutList />} />
        <Route path="payouts/new" element={<PayoutCreate />} />
        <Route path="bank-accounts" element={<BankAccounts />} />
        <Route path="kyb" element={<KybSubmit />} />
        <Route path="kyc" element={<KycSubmit />} />
        <Route path="developers/api-keys" element={<ApiKeys />} />
        <Route path="developers/webhooks" element={<Webhooks />} />
        <Route path="settings" element={<Settings />} />
        <Route path="announcements" element={<AnnouncementCenter />} />
      </Route>
    </Routes>
  )
}
